'use client';

import { faSpinnerThird } from '@fortawesome/pro-light-svg-icons';
import { faX } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Modal, ModalContent, Select } from '@pickleballinc/react-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { useGetAllScoreFormats } from '@/app/(default)/(leagues)/leagues/[slug]/session/[sessionId]/matchdate/[matchDateId]/queries';
import { useSaveChangeScoreFormat } from '@/modules/league/new-components/matches-components/hooks';
import { ScoreFormat } from '@/modules/league/new-components/types';
import { useLoader } from '@/modules/league/PickleballLoaderContext';

interface ChangeScoreFormatProps {
  openModal: boolean;
  setIsModalOpen: (show: boolean) => void;
  title: string;
  currentScoreFormat: string;
  isMatchesPage: boolean;
  matchDateId: string;
  scoreId: string;
  poolId: string;
}

const ChangeScoreFormatModal = ({
  openModal,
  setIsModalOpen,
  title,
  currentScoreFormat,
  isMatchesPage,
  matchDateId,
  scoreId,
  poolId,
}: ChangeScoreFormatProps) => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useGetAllScoreFormats();
  const scoreFormats = data?.scoreFormatsData || [];

  const { setLoading } = useLoader();
  const { mutate: saveChangeScoreFormat } = useSaveChangeScoreFormat();

  const [selectedOption, setSelectedOption] = useState<ScoreFormat | null>();

  useEffect(() => {
    if (scoreFormats.length > 0) {
      setSelectedOption(
        scoreFormats.find(
          (scoreFormat) => scoreFormat.Shorthand.toLowerCase() === currentScoreFormat.toLowerCase()
        ) || null
      );
    }
  }, [scoreFormats, openModal]);

  if (!openModal) return null;

  const options = scoreFormats.map((scoreFormat) => ({
    value: scoreFormat.ScoreFormatID,
    label: scoreFormat.Shorthand,
    originalItem: selectedOption,
  }));

  type ScoreFormatOption = {
    value: number;
    label: string;
  };

  return (
    <Modal open={openModal} onOpenChange={setIsModalOpen}>
      <ModalContent className='flex w-[calc(100%-16px)] flex-col gap-4 !p-6 !outline-none md:max-w-[530px]'>
        <div className='relative'>
          <h2 className='text-center text-base font-bold text-gray-900'>{title}</h2>
          <div className='flex flex-col pt-2 gap-2'>
            <p>
              Select a new score format for this {isMatchesPage ? 'match' : 'group'} and click Save.
            </p>
            {currentScoreFormat ? (
              <p className='font-semibold'>
                Current Score Format: <span className='font-normal'>{currentScoreFormat}</span>
              </p>
            ) : null}
            <div className='mt-4 hidden w-full border-t-[0.5px] border-gray-200 px-2 md:block' />
          </div>
          <div
            className='absolute -right-2 top-0 flex size-6 items-center justify-center rounded-md border border-solid border-gray-300 bg-white p-1 hover:cursor-pointer hover:bg-gray-100'
            onClick={() => setIsModalOpen(false)}
          >
            <FontAwesomeIcon icon={faX} className='text-gray-400' height={20} />
          </div>
        </div>

        <div className='flex text-left text-base font-semibold text-gray-900'>
          New Score Format:
        </div>
        {isLoading ? (
          <div className='flex m-auto'>
            <FontAwesomeIcon icon={faSpinnerThird} spin size='2x' />
          </div>
        ) : (
          <div className='flex flex-row w-full gap-2 pb-4'>
            <div className='w-full'>
              <Select
                options={options}
                styles={{
                  control: (provided, state) => ({
                    ...provided,
                    borderColor: state.isFocused ? '#f63d68' : '#ccc',
                    boxShadow: state.isFocused ? '0 0 0 1px #f63d68' : 'none',
                    '&:hover': {
                      borderColor: '#f63d68',
                    },
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isSelected
                      ? '#f63d68B3'
                      : state.isFocused
                        ? '#f63d6833'
                        : 'white',
                  }),
                }}
                onChange={(newValue: unknown) => {
                  const option = newValue as ScoreFormatOption | null;

                  if (option) {
                    const selectedScoreFormat = scoreFormats.find(
                      (scoreFormat) => scoreFormat.ScoreFormatID === option.value
                    );
                    setSelectedOption(selectedScoreFormat || null);
                  } else {
                    setSelectedOption(null);
                  }
                }}
                value={
                  selectedOption
                    ? {
                        value: selectedOption.ScoreFormatID,
                        label: selectedOption.Shorthand,
                      }
                    : null
                }
              />
            </div>
            <Button
              variant='primary'
              className='!rounded-full p-2 !text-xs font-semibold uppercase md:mb-1 md:!text-sm bg-leagues-500 text-white md:!px-4'
              onClick={() => {
                if (selectedOption) {
                  setLoading(true);
                  saveChangeScoreFormat(
                    { matchDateId, scoreId, poolId, scoreFormatId: selectedOption.ScoreFormatID },
                    {
                      onSuccess: (data) => {
                        if (data.message) {
                          toast.success(data.message);
                        }
                      },
                      onError: () => {
                        toast.error('Unable to update the score format. Please try again.');
                        setLoading(false);
                      },
                    }
                  );
                  if (isMatchesPage) {
                    queryClient.invalidateQueries(['league_matches']);
                  } else {
                    queryClient.invalidateQueries(['league_groups']);
                  }
                  setIsModalOpen(false);
                }
              }}
              disabled={!selectedOption}
            >
              Change
            </Button>
          </div>
        )}
      </ModalContent>
    </Modal>
  );
};

export default ChangeScoreFormatModal;
